import { get, post, put, del } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_NAME = 'api';

// Helper function to make API calls with authentication
const makeApiCall = async (method, path, data = null) => {
  try {
    // Get the current auth session to include JWT token
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    
    const baseOptions = {
      apiName: API_NAME,
      path,
      options: {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      }
    };

    // Add body data for POST/PUT requests
    if (data && (method === 'POST' || method === 'PUT')) {
      // Don't stringify here - AWS Amplify will handle JSON serialization
      baseOptions.options.body = data;
    }

    console.log(`Making ${method} request to ${path}`, { 
      hasToken: !!token, 
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'none',
      hasData: !!data,
      dataPreview: data ? Object.keys(data) : 'none'
    });

    let response;
    switch (method) {
      case 'GET':
        response = await get(baseOptions).response;
        break;
      case 'POST':
        response = await post(baseOptions).response;
        break;
      case 'PUT':
        response = await put(baseOptions).response;
        break;
      case 'DELETE':
        response = await del(baseOptions).response;
        break;
      default:
        throw new Error(`Unsupported method: ${method}`);
    }

    const result = await response.body.json();
    console.log(`${method} ${path} response:`, result);
    return { data: result };
  } catch (error) {
    console.error(`API ${method} ${path} error:`, error);
    throw error;
  }
};

// User Service
export const userService = {
  getProfile: (userId) => makeApiCall('GET', `/users/${userId}`),
  createProfile: () => makeApiCall('POST', '/users'),
  updateProfile: (userId, data) => makeApiCall('PUT', `/users/${userId}`, data),
  deleteAccount: (userId) => makeApiCall('DELETE', `/users/${userId}`),
  getMyProfile: () => makeApiCall('GET', '/users/me'),
  getUserStats: (userId = null) => {
    const url = userId ? `/users/stats?user_id=${userId}` : '/users/stats';
    return makeApiCall('GET', url);
  },
};

// Story Service
export const storyService = {
  getStories: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const path = queryString ? `/stories?${queryString}` : '/stories';
    console.log('storyService.getStories: params =', params);
    console.log('storyService.getStories: queryString =', queryString);
    console.log('storyService.getStories: path =', path);
    return makeApiCall('GET', path);
  },
  getStory: (storyId) => makeApiCall('GET', `/stories/${storyId}`),
  createStory: (data) => makeApiCall('POST', '/stories', data),
  updateStory: (storyId, data) => makeApiCall('PUT', `/stories/${storyId}`, data),
  deleteStory: (storyId) => makeApiCall('DELETE', `/stories/${storyId}`),
  getMyStories: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    if (params.status) queryParams.append('status', params.status);
    
    const queryString = queryParams.toString();
    const url = queryString ? `/stories/my?${queryString}` : '/stories/my';
    return makeApiCall('GET', url);
  },
  publishStory: (storyId) => makeApiCall('PUT', `/stories/${storyId}`, { status: 'published' }),
  unpublishStory: (storyId) => makeApiCall('PUT', `/stories/${storyId}`, { status: 'draft' }),
};

// Comments Service
export const commentService = {
  getComments: (storyId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const path = queryString ? `/stories/${storyId}/comments?${queryString}` : `/stories/${storyId}/comments`;
    return makeApiCall('GET', path);
  },
  createComment: (storyId, data) => makeApiCall('POST', `/stories/${storyId}/comments`, data),
  deleteComment: (storyId, commentId) => makeApiCall('DELETE', `/stories/${storyId}/comments/${commentId}`),
  getReplies: (storyId, parentCommentId) => {
    const params = new URLSearchParams({ parent_comment_id: parentCommentId }).toString();
    return makeApiCall('GET', `/stories/${storyId}/comments?${params}`);
  },
};

// Admin Service
export const adminService = {
  getStats: () => makeApiCall('GET', '/admin/stats'),
  getUsers: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const path = queryString ? `/admin/users?${queryString}` : '/admin/users';
    return makeApiCall('GET', path);
  },
  updateUser: (userId, data) => makeApiCall('PUT', `/admin/users/${userId}`, data),
  deleteUser: (userId) => makeApiCall('DELETE', `/admin/users/${userId}`),
  
  // New pending user management methods
  getPendingUsers: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const path = queryString ? `/admin/users/pending?${queryString}` : '/admin/users/pending';
    return makeApiCall('GET', path);
  },
  approveUser: (userId) => makeApiCall('POST', `/admin/users/${userId}/approve`),
  suspendUser: (userId) => makeApiCall('POST', `/admin/users/${userId}/suspend`),
  bulkApproveUsers: (userIds) => makeApiCall('POST', '/admin/users/bulk-approve', { user_ids: userIds }),
  
  getStories: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const path = queryString ? `/admin/stories?${queryString}` : '/admin/stories';
    return makeApiCall('GET', path);
  },
  deleteStory: (storyId) => makeApiCall('DELETE', `/admin/stories/${storyId}`),
  getComments: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const path = queryString ? `/admin/comments?${queryString}` : '/admin/comments';
    return makeApiCall('GET', path);
  },
  deleteComment: (commentId) => makeApiCall('DELETE', `/admin/comments/${commentId}`),

  // Event Results Management
  getParticipantsWithStories: (eventId) => makeApiCall('GET', `/admin/events/${eventId}/results/participants`),
  getEventResults: (eventId) => makeApiCall('GET', `/admin/events/${eventId}/results`),
  setWinner: (eventId, winnerData) => makeApiCall('PUT', `/admin/events/${eventId}/results/winner`, winnerData),
  setFinalists: (eventId, finalistsData) => makeApiCall('PUT', `/admin/events/${eventId}/results/finalists`, finalistsData),
  publishResults: (eventId) => makeApiCall('PUT', `/admin/events/${eventId}/results/publish`),
  
  // Judge Management
  getJudges: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const path = queryString ? `/admin/judges?${queryString}` : '/admin/judges';
    return makeApiCall('GET', path);
  },
  makeUserJudge: (userId) => makeApiCall('POST', `/admin/users/${userId}/make-judge`),
  removeJudgeRole: (userId) => makeApiCall('POST', `/admin/users/${userId}/remove-judge`),
  unpublishResults: (eventId) => makeApiCall('PUT', `/admin/events/${eventId}/results/unpublish`),
  deleteResults: (eventId) => makeApiCall('DELETE', `/admin/events/${eventId}/results`)
};

// Support service
export const supportService = {
  sendSupportRequest: (requestData) => makeApiCall('POST', '/support', requestData)
};

// Events Service
export const eventsService = {
  // Get all events (public endpoint)
  getEvents: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const path = queryString ? `/events?${queryString}` : '/events';
    return makeApiCall('GET', path);
  },

  // Get lightweight events summary (optimized for listing pages)
  getEventsSummary: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const path = queryString ? `/events/summary?${queryString}` : '/events/summary';
    return makeApiCall('GET', path);
  },

  // Get active events only (lightweight for story creation)
  getActiveEvents: () => {
    return makeApiCall('GET', '/events/dropdown');
  },

  // Get event basic info (lightweight)
  getEventInfo: (eventId) => makeApiCall('GET', `/events/${eventId}/info`),

  // Get event submissions (lightweight)
  getEventSubmissions: (eventId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const path = queryString ? `/events/${eventId}/submissions?${queryString}` : `/events/${eventId}/submissions`;
    return makeApiCall('GET', path);
  },

  // Get event participants (lightweight)
  getEventParticipants: (eventId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const path = queryString ? `/events/${eventId}/participants?${queryString}` : `/events/${eventId}/participants`;
    return makeApiCall('GET', path);
  },

  // Get specific event with participants
  getEvent: (eventId) => makeApiCall('GET', `/events/${eventId}`),

  // Create new event (admin only)
  createEvent: (eventData) => makeApiCall('POST', '/events', eventData),

  // Update event (admin only)
  updateEvent: (eventId, eventData) => makeApiCall('PUT', `/events/${eventId}`, eventData),

  // Delete event (admin only)
  deleteEvent: (eventId) => makeApiCall('DELETE', `/events/${eventId}`),

  // Join event (authenticated users)
  joinEvent: (eventId) => makeApiCall('POST', `/events/${eventId}/actions`, { action: 'join' }),

  // Leave event (authenticated users)
  leaveEvent: (eventId) => makeApiCall('POST', `/events/${eventId}/actions`, { action: 'leave' }),

  // Get events by status
  getEventsByStatus: async (status) => {
    try {
      const queryString = new URLSearchParams({ status }).toString();
      const path = `/events?${queryString}`;
      return makeApiCall('GET', path);
    } catch (error) {
      console.error('Error fetching events by status:', error);
      throw error;
    }
  },

  // Get pending events
  getPendingEvents: () => {
    const queryString = new URLSearchParams({ status: 'pending' }).toString();
    const path = `/events?${queryString}`;
    return makeApiCall('GET', path);
  },

  // Get closed events
  getClosedEvents: () => {
    const queryString = new URLSearchParams({ status: 'closed' }).toString();
    const path = `/events?${queryString}`;
    return makeApiCall('GET', path);
  },

  // Check if user has joined an event
  checkUserParticipation: async (eventId) => {
    try {
      const response = await makeApiCall('GET', `/events/${eventId}`);
      // The backend should include user participation status in the response
      return response.data?.user_participating || false;
    } catch (error) {
      console.error('Error checking user participation:', error);
      return false;
    }
  },

  // Public Results Management
  getPublishedResults: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const path = queryString ? `/events/results?${queryString}` : '/events/results';
    return makeApiCall('GET', path);
  },

  // Get lightweight results summary (optimized for results listing page)
  getResultsSummary: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const path = queryString ? `/events/results/summary?${queryString}` : '/events/results/summary';
    return makeApiCall('GET', path);
  },

  getEventResults: (eventId) => makeApiCall('GET', `/events/${eventId}/results`)
};

export const resultsService = {
  getLatestResultSnapshot: () => makeApiCall('GET', '/events/results/latest-snapshot'),
};
// Legacy exports for backward compatibility
export const userAPI = userService;
export const storiesAPI = storyService;
export const commentsAPI = commentService;
export const adminAPI = adminService;
export const eventsAPI = eventsService;
export const resultsApi = resultsService;